/**
 * The connection form's label vocabulary (Option C, C2; the prototype's words, sentence case, no colons). One module
 * names each field per engine and mode, so the label on screen and the field's "… is required" message always agree
 * (`connectionFieldRules.ts` builds its messages from here). The table is in `Docs/ConnectionDialog.md`.
 */
import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { storageModeOnScreen } from '../../components/azureStorageFormRules';

type Values = Partial<ConnectionFormValues>;
type Auth = ConnectionFormValues['authentication'] | undefined;

/** The field that says where the connection goes: Server, Host, Database file, Cluster URL, Storage account … */
export function targetLabel(values: Values): string {
  switch (values.engine as DatabaseEngine | undefined) {
    case 'sqlite': return 'Database file';
    case 'kusto': return 'Cluster URL';
    // The mode on screen (C3: the "Connect using" pick included, so the message names what the row's label says).
    // "Single resource" writes no scope until a URL is pasted (`storageModeValues`), so an absent scope reads as that pick.
    case 'azurestorage': return storageModeOnScreen(values) === 'resource' || values.azureStorageScope === undefined ? 'Resource URL' : 'Storage account';
    case 'pgsql':
    case 'mysql':
    case 'oracle': return 'Host';
    default: return 'Server';
  }
}

/** The user field: Login (SQL Server), User (PostgreSQL, MySQL, Oracle), Client ID (a service principal). */
export function loginLabel(engine: DatabaseEngine | undefined): string {
  if (engine === 'kusto' || engine === 'azurestorage') return 'Client ID';
  if (engine === 'pgsql' || engine === 'mysql' || engine === 'oracle') return 'User';
  return 'Login';
}

/** The secret field: Password, or the Azure credential the auth mode asks for. */
export function secretLabel(engine: DatabaseEngine | undefined, auth: Auth): string {
  if (engine === 'azurestorage') {
    if (auth === 'Account Key') return 'Account key';
    if (auth === 'Shared Access Signature') return 'SAS token or connection string';
  }
  if (auth === 'Service Principal') return 'Client secret';
  return 'Password';
}

/** The label of a rule-owned field as the form shows it for these values ("Still needed: Password", C3). */
export function fieldRuleLabel(field: string, values: Values): string {
  const engine = values.engine as DatabaseEngine | undefined;
  switch (field) {
    case 'server': return targetLabel(values);
    case 'port': return 'Port';
    case 'login': return loginLabel(engine);
    case 'password': return secretLabel(engine, values.authentication);
    case 'database': return 'Database';
    case 'entraTenantId': return 'Tenant ID';
    case 'oracleTnsDescriptor': return 'TNS descriptor';
    case 'oracleTnsAlias': return 'TNS alias';
    case 'sshTunnel.host': return 'SSH host';
    case 'sshTunnel.port': return 'SSH port';
    case 'sshTunnel.user': return 'SSH user';
    case 'sshTunnel.auth': return 'Key file';
    default: return field;
  }
}

/** The section that holds the target: Server, File, Cluster or Storage. */
export function targetSectionTitle(engine: DatabaseEngine | undefined): { id: 'server' | 'file' | 'cluster' | 'storage'; title: string } {
  if (engine === 'sqlite') return { id: 'file', title: 'File' };
  if (engine === 'kusto') return { id: 'cluster', title: 'Cluster' };
  if (engine === 'azurestorage') return { id: 'storage', title: 'Storage' };
  return { id: 'server', title: 'Server' };
}

/** Engines with a Security section (encryption / SSL and the SSH tunnel); the others have nothing to put there. */
export function hasSecuritySection(engine: DatabaseEngine | undefined): boolean {
  return engine === 'mssql' || engine === 'pgsql' || engine === 'mysql' || engine === 'oracle';
}

/** A per-engine example for the empty Name field (prototype). */
export const NAME_PLACEHOLDERS: Partial<Record<DatabaseEngine, string>> = {
  mssql: 'e.g. Prod orders',
  pgsql: 'e.g. analytics-dev',
  mysql: 'e.g. shop-db',
  oracle: 'e.g. erp-prod',
  sqlite: 'e.g. local-cache',
  kusto: 'e.g. telemetry',
  azurestorage: 'e.g. assets',
};
